import { Tag } from 'lucide-react'
import type { KeyboardEvent, MouseEvent } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover'
import { pricingRuleLabel, pricingRuleName } from '@/app/lib/pricing-display'
import type { PromotionPricingRule } from '@/app/services/pos.service'

interface ProductPromotionPopoverProps {
  pricing: PromotionPricingRule
}

function formatPromotionDate(value?: string | null): string | null {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleString('es-MX', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

export function ProductPromotionPopover({ pricing }: ProductPromotionPopoverProps) {
  const name = pricingRuleName(pricing) || 'Promoción'
  const rule = pricingRuleLabel(pricing)
  const startsAt = formatPromotionDate(pricing.starts_at)
  const endsAt = formatPromotionDate(pricing.ends_at)

  const stopCardClick = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation()
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Ver promoción ${name}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-purple-200 bg-purple-100 px-2.5 py-1.5 text-xs font-semibold text-purple-700 transition-colors hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
          onClick={stopCardClick}
          onKeyDown={stopCardClick}
        >
          <Tag className="h-4 w-4 shrink-0" />
          Promoción
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(320px,calc(100vw-2rem))] rounded-lg border border-purple-200 bg-card p-0 text-card-foreground shadow-md"
        side="top"
        align="start"
        onClick={stopCardClick}
        onKeyDown={stopCardClick}
      >
        <div className="border-b border-purple-100 bg-purple-50 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">
            Promoción vigente
          </p>
        </div>
        <div className="space-y-2 p-3">
          <p className="text-sm font-semibold text-card-foreground">{name}</p>
          {rule && <p className="text-sm text-card-foreground">{rule}</p>}
          {(startsAt || endsAt) && (
            <div className="text-xs text-card-description">
              <p className="font-medium text-card-foreground/80">Vigencia</p>
              {startsAt && <p>Desde: {startsAt}</p>}
              {endsAt && <p>Hasta: {endsAt}</p>}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
